import { Hono } from 'hono';
import { PitchKalmanFilter } from './src/kalman-filter.ts';
import { analyzeFormants, type FormantAnalysis } from './src/harmonic-analysis.ts';
import { alignPitchContourDTW, type DTWAlignmentResult } from './src/dtw-aligner.ts';
import { classifyAudioFrame, FrameClassification, type VADResult } from './src/vad.ts';

export { PitchKalmanFilter, analyzeFormants, type FormantAnalysis, alignPitchContourDTW, type DTWAlignmentResult, classifyAudioFrame, FrameClassification, type VADResult };

/**
 * Core Vedic Phonetics & Pitch Analysis Package (@sovereign/vedic-phonetics)
 * Provides DSP pitch auto-correlation algorithm for Vedic Swara accents (Udatta, Anudatta, Svarita),
 * Devanagari phonetic parsing, and Hono microservice endpoints.
 */

export const vedicPhoneticsApp = new Hono();

vedicPhoneticsApp.get('/api/phonetics/health', (c) => {
  return c.json({
    app: 'vedic-phonetics',
    status: 'ACTIVE',
    mode: 'UNIFIED_VEDIC_PHONETICS_ENGINE',
    version: '1.0.0',
  });
});

vedicPhoneticsApp.get('/api/phonetics/swara-rules', (c) => {
  return c.json({
    accents: [
      { name: 'Udatta', mark: '॥', pitchDirection: 'HIGH', tone: 'High Pitch Accent' },
      { name: 'Anudatta', mark: '_ ', pitchDirection: 'LOW', tone: 'Low Pitch Accent' },
      { name: 'Svarita', mark: '|\'', pitchDirection: 'FALLING', tone: 'Combined Pitch Accent' },
    ],
  });
});

export type VedicPhoneticsApi = typeof vedicPhoneticsApp;
export default vedicPhoneticsApp;

export interface SpectralCentroidResult {
  centroidHz: number;
  isNasalized: boolean; // Anusvara (ṁ) resonance
  isAspirated: boolean; // Visarga (ḥ) post-vocalic aspiration
  resonanceType: 'Anusvara' | 'Visarga' | 'Clear';
  highEnergyRatio: number;
}

export interface PitchAnalysisResult {
  pitchHz: number;
  noteName: string;
  swara: 'Udatta' | 'Anudatta' | 'Svarita' | 'Sama';
  centsDeviation: number;
  clarity: number;
  resonance?: SpectralCentroidResult;
}

export interface PitchDetectionResult {
  pitchHz: number;
  noteName: string;
  swara: 'Udatta' | 'Anudatta' | 'Svarita' | 'Sama';
  centsDeviation: number;
  clarity: number;
  fundamentalF0: number;
  spectralCentroid: number;
  hpsPitchHz?: number;
  vowelEstimate?: 'a' | 'i' | 'u' | 'e' | 'o' | 'unknown';
  resonance?: SpectralCentroidResult;
}

/**
 * Calculates spectral centroid & nasal/aspirated formants from time-domain audio buffer.
 * - Anusvara (ṁ): Focused nasal resonance around 1000-2200 Hz with energy concentration.
 * - Visarga (ḥ): High spectral centroid (>3200 Hz) due to post-vocalic breath/aspiration noise.
 */
export function calculateSpectralCentroid(
  buffer: Float32Array,
  sampleRate: number
): SpectralCentroidResult {
  if (buffer.length === 0) {
    return { centroidHz: 0, isNasalized: false, isAspirated: false, resonanceType: 'Clear', highEnergyRatio: 0 };
  }

  // Calculate Zero Crossings & High Frequency energy ratio as fast time-domain spectral centroid proxy
  let zeroCrossings = 0;
  let totalEnergy = 0;
  let highFreqEnergy = 0;
  let nasalFreqEnergy = 0;

  for (let i = 0; i < buffer.length; i++) {
    const val = buffer[i];
    const absVal = Math.abs(val);
    totalEnergy += absVal;

    if (i > 0 && ((buffer[i - 1] >= 0 && val < 0) || (buffer[i - 1] < 0 && val >= 0))) {
      zeroCrossings++;
    }

    // High pass filter difference calculation
    if (i > 1) {
      const diff = Math.abs(buffer[i] - buffer[i - 1]);
      highFreqEnergy += diff;
      if (i > 2) {
        const doubleDiff = Math.abs(buffer[i] - 2 * buffer[i - 1] + buffer[i - 2]);
        nasalFreqEnergy += doubleDiff;
      }
    }
  }

  const avgEnergy = totalEnergy / buffer.length;
  if (avgEnergy < 0.008) {
    return { centroidHz: 0, isNasalized: false, isAspirated: false, resonanceType: 'Clear', highEnergyRatio: 0 };
  }

  // Estimate zero-crossing rate centroid frequency
  const zcr = zeroCrossings / buffer.length;
  const estimatedCentroidHz = (zcr * sampleRate) / 2;

  const highEnergyRatio = totalEnergy > 0 ? (highFreqEnergy / totalEnergy) : 0;
  const nasalEnergyRatio = totalEnergy > 0 ? (nasalFreqEnergy / totalEnergy) : 0;

  // Anusvara (ṁ) detection: controlled nasal resonance with moderate high energy ratio
  const isNasalized = nasalEnergyRatio > 0.45 && highEnergyRatio < 1.2 && estimatedCentroidHz >= 800 && estimatedCentroidHz <= 2800;

  // Visarga (ḥ) detection: unvoiced post-vocalic aspiration breath with elevated spectral centroid (>3200 Hz)
  const isAspirated = estimatedCentroidHz > 3200 || highEnergyRatio > 1.45;

  let resonanceType: 'Anusvara' | 'Visarga' | 'Clear' = 'Clear';
  if (isAspirated) {
    resonanceType = 'Visarga';
  } else if (isNasalized) {
    resonanceType = 'Anusvara';
  }

  return {
    centroidHz: Math.round(estimatedCentroidHz),
    isNasalized,
    isAspirated,
    resonanceType,
    highEnergyRatio: Math.round(highEnergyRatio * 100) / 100,
  };
}

export function detectVedicSwaraPitch(
  buffer: Float32Array,
  sampleRate: number,
  baseFrequencyHz: number = 220
): PitchAnalysisResult {
  // Auto-correlation algorithm for pitch detection
  let sumOfSquares = 0;
  for (let i = 0; i < buffer.length; i++) {
    sumOfSquares += buffer[i] * buffer[i];
  }
  const rms = Math.sqrt(sumOfSquares / buffer.length);
  const resonance = calculateSpectralCentroid(buffer, sampleRate);

  if (rms < 0.01) {
    return { pitchHz: 0, noteName: 'Silence', swara: 'Sama', centsDeviation: 0, clarity: 0, resonance };
  }

  let bestLag = -1;
  let maxCorr = -1;
  const minLag = Math.floor(sampleRate / 800);
  const maxLag = Math.floor(sampleRate / 80);

  for (let lag = minLag; lag <= maxLag; lag++) {
    let corr = 0;
    for (let i = 0; i < buffer.length - lag; i++) {
      corr += buffer[i] * buffer[i + lag];
    }
    if (corr > maxCorr) {
      maxCorr = corr;
      bestLag = lag;
    }
  }

  if (bestLag === -1 || maxCorr / sumOfSquares < 0.3) {
    return { pitchHz: 0, noteName: 'Unclear', swara: 'Sama', centsDeviation: 0, clarity: 0, resonance };
  }

  const pitchHz = sampleRate / bestLag;
  const semitonesFromBase = 12 * Math.log2(pitchHz / baseFrequencyHz);
  const centsDeviation = Math.round((semitonesFromBase - Math.round(semitonesFromBase)) * 100);

  let swara: 'Udatta' | 'Anudatta' | 'Svarita' | 'Sama' = 'Sama';
  if (centsDeviation > 25) {
    swara = 'Udatta'; // High pitch accent
  } else if (centsDeviation < -25) {
    swara = 'Anudatta'; // Low pitch accent
  } else if (Math.abs(centsDeviation) > 10) {
    swara = 'Svarita'; // Combined/falling accent
  }

  return {
    pitchHz: Math.round(pitchHz * 10) / 10,
    noteName: getNoteName(pitchHz),
    swara,
    centsDeviation,
    clarity: Math.min(100, Math.round((maxCorr / sumOfSquares) * 100)),
    resonance,
  };
}

/**
 * AudioWorklet Processor Source Code string for off-thread real-time pitch processing
 */
export const VEDIC_PHONETICS_PITCH_WORKLET_CODE = `
class VedicPitchProcessor extends AudioWorkletProcessor {
  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input && input[0] && input[0].length > 0) {
      const channelData = input[0];
      this.port.postMessage({
        samples: Array.from(channelData),
        timestamp: currentTime
      });
    }
    return true;
  }
}
registerProcessor('vedic-pitch-processor', VedicPitchProcessor);
`;

function getNoteName(frequencyHz: number): string {
  const noteStrings = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const midiNote = Math.round(69 + 12 * Math.log2(frequencyHz / 440));
  const noteIndex = (midiNote % 12 + 12) % 12;
  const octave = Math.floor(midiNote / 12) - 1;
  return `${noteStrings[noteIndex]}${octave}`;
}

export function parseVedicAccents(text: string): { syllable: string; swaraMark?: string }[] {
  const result: { syllable: string; swaraMark?: string }[] = [];
  const chars = Array.from(text);
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    if (char === '\u0951') {
      // Udatta accent mark (vertical line above)
      if (result.length > 0) result[result.length - 1].swaraMark = 'Udatta';
    } else if (char === '\u0952') {
      // Anudatta accent mark (horizontal line below)
      if (result.length > 0) result[result.length - 1].swaraMark = 'Anudatta';
    } else {
      result.push({ syllable: char });
    }
  }
  return result;
}

/**
  * Harmonic Product Spectrum (HPS) pitch calculation algorithm.
  */
export function computeHarmonicProductSpectrum(
  pcmBuffer: Float32Array,
  sampleRate: number,
  minFreq: number = 70,
  maxFreq: number = 800,
  harmonics: number = 3
): number {
  if (!pcmBuffer || pcmBuffer.length < 64) return 0;
  const fftSize = pcmBuffer.length;
  const halfSize = Math.floor(fftSize / 2);
  const magnitudes = new Float32Array(halfSize);

  // Fast DFT magnitude calculation
  for (let k = 0; k < halfSize; k++) {
    let real = 0;
    let imag = 0;
    for (let n = 0; n < fftSize; n++) {
      const angle = (2 * Math.PI * k * n) / fftSize;
      real += pcmBuffer[n] * Math.cos(angle);
      imag -= pcmBuffer[n] * Math.sin(angle);
    }
    magnitudes[k] = Math.sqrt(real * real + imag * imag);
  }

  // Multiply downsampled spectra (Harmonic Product Spectrum)
  const hps = new Float32Array(Math.floor(halfSize / harmonics));
  for (let i = 0; i < hps.length; i++) {
    let product = magnitudes[i];
    for (let h = 2; h <= harmonics; h++) {
      product *= magnitudes[i * h] || 1;
    }
    hps[i] = product;
  }

  let maxVal = 0;
  let bestBin = 0;
  const minBin = Math.floor((minFreq * fftSize) / sampleRate);
  const maxBin = Math.min(Math.floor((maxFreq * fftSize) / sampleRate), hps.length - 1);

  for (let b = minBin; b <= maxBin; b++) {
    if (hps[b] > maxVal) {
      maxVal = hps[b];
      bestBin = b;
    }
  }

  return bestBin > 0 ? (bestBin * sampleRate) / fftSize : 0;
}

/**
 * Unified DSP typed contract: analyzes PCM frame using autocorrelation,
 * harmonic product spectrum (HPS), and spectral centroid.
 */
export function analyzeFrame(
  pcmData: Float32Array,
  sampleRate: number = 44100,
  baseFrequencyHz: number = 220
): PitchDetectionResult {
  const pitchResult = detectVedicSwaraPitch(pcmData, sampleRate, baseFrequencyHz);
  const formants = analyzeFormants(pcmData, sampleRate, pitchResult.pitchHz);
  const hpsPitchHz = computeHarmonicProductSpectrum(pcmData, sampleRate);

  return {
    pitchHz: pitchResult.pitchHz,
    noteName: pitchResult.noteName,
    swara: pitchResult.swara,
    centsDeviation: pitchResult.centsDeviation,
    clarity: pitchResult.clarity,
    fundamentalF0: pitchResult.pitchHz,
    spectralCentroid: formants.spectralCentroid || (pitchResult.resonance?.centroidHz ?? 0),
    hpsPitchHz: Math.round(hpsPitchHz * 10) / 10,
    vowelEstimate: formants.vowelEstimate,
    resonance: pitchResult.resonance
  };
}

